// TelcoTrack - Diary Session / Trigger service
// Spravuje ruční otevření modalu stavebního deníku z UI. Deník se už neotevírá
// automaticky po commitu objektu; je zodpovědností stavbyvedoucího otevřít jej
// v samostatné záložce/menu.

import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { ConstructionDiaryApiService } from './construction-diary.service';
import { ConstructionDiary } from '../models/construction-diary.model';

interface PendingDiary {
  entry: ConstructionDiary;
  polygonId: string;
}

@Injectable({ providedIn: 'root' })
export class DiarySessionService {
  /** UI se subscribuje na tento stream; když není null → modal musí otevřít. */
  private readonly pendingSubject = new BehaviorSubject<PendingDiary | null>(null);
  readonly pending$: Observable<PendingDiary | null> = this.pendingSubject.asObservable();

  /** Polygon×date už byl v této session prezentován / přeskočen. */
  private readonly triggeredKeys = new Set<string>();

  /** Prevent duplicitních volání během čekání na odpověď. */
  private readonly inflightKeys = new Set<string>();

  constructor(
    private readonly diaryApi: ConstructionDiaryApiService,
  ) {}

  /** `YYYY-MM-DD` v Europe/Prague — zarovnáno s backendem. */
  private todayPragueIso(): string {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Prague',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  }

  private key(polygonId: string, entryDate: string): string {
    return `${polygonId}|${entryDate}`;
  }

  /**
   * Dříve voláno po úspěšném commitu v mapě. Auto-open je záměrně vypnutý,
   * protože stavbyvedoucí otevírá stavební deník ručně v samostatném menu.
   */
  async maybeTriggerForPolygon(_polygonId: string | null | undefined): Promise<void> {
    return;
  }

  /** Skrýt modal po uložení / zavření. */
  dismiss(): void {
    this.pendingSubject.next(null);
  }

  /** Re-fetch záznamu (např. po vnějším update). */
  setEntry(entry: ConstructionDiary, polygonId: string): void {
    this.pendingSubject.next({ entry, polygonId });
  }

  /** Explicitní trigger z UI (např. tlačítko „Otevřít deník" v toolbaru mapy). */
  async openForPolygon(polygonId: string): Promise<void> {
    const entryDate = this.todayPragueIso();
    const k = this.key(polygonId, entryDate);
    this.inflightKeys.add(k);
    try {
      const entry = await firstValueFrom(this.diaryApi.getOrCreateToday(polygonId, entryDate));
      if (entry) {
        this.triggeredKeys.add(k);
        this.pendingSubject.next({ entry, polygonId });
      }
    } finally {
      this.inflightKeys.delete(k);
    }
  }
}
